import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

// Types for the stakeholder relation response
export type ColumnType = 'LONG' | 'SIMPLE_TEXT' | 'LONG_LIST' | 'STAKEHOLDER_INFO';

export interface StakeholderColumn {
  columnId: string;
  displayName: string;
  columnType: ColumnType;
  parentColumnId: string | null;
  metadata: unknown | null;
}

export interface StakeholderInfo {
  employees: unknown[];
  stakeholderRelation: string | null;
}

export interface StakeholderRow {
  ID: number;
  FUNCTION: string;
  HIERARCHY: number[];
  PROPOSER: StakeholderInfo;
  APPROVER: StakeholderInfo;
  PUBLISHER: StakeholderInfo;
}

export interface StakeholderRelationResponse {
  columns: StakeholderColumn[];
  rows: StakeholderRow[];
  pivotFields: null;
}

// Types for the stakeholder relation update request
export interface StakeholderAssignment {
  employeeIds: number[];
  stakeholderRelation: string | null;
}

export interface StakeholderAssignments {
  PUBLISHER: StakeholderAssignment;
  PROPOSER: StakeholderAssignment;
  APPROVER: StakeholderAssignment;
}

export interface StakeholderMapping {
  jobFunctionId: number;
  stakeholderAssignments: StakeholderAssignments;
}

export interface UpdateStakeholderRelationRequest {
  stakeholderMappings: StakeholderMapping[];
}

// Types for stakeholder map response
export type StakeholderMapColumnType = 'EMPLOYEE' | 'EMPLOYEE_LIST';

export interface StakeholderMapColumn {
  columnId: string;
  displayName: string;
  columnType: StakeholderMapColumnType;
  parentColumnId: string | null;
  metadata: unknown | null;
}

export interface Employee {
  id: number;
  companyId: number | null;
  displayName: string;
  profilePhoto: string;
  designation: string;
  ownerType: 'OWNER' | 'COLLABORATOR' | null;
  employeeFunction: string | null;
  email: string;
  personalEmail: string;
  workEmail: string | null;
  preferredName: string;
}

export interface StakeholderMapRow {
  PUBLISHER: Employee[];
  EMPLOYEE: Employee;
  PROPOSER: Employee[];
  APPROVER: Employee[];
}

export interface StakeholderMapResponse {
  columns: StakeholderMapColumn[];
  rows: StakeholderMapRow[];
}

// Types for stakeholder map update request
export interface StakeholderMapAssignment {
  PROPOSER?: number[];
  APPROVER?: number[];
  PUBLISHER?: number[];
}

export interface StakeholderMapMapping {
  employeeId: number;
  stakeholderAssignments: StakeholderMapAssignment;
}

export interface UpdateStakeholderMapRequest {
  stakeholderMappings: StakeholderMapMapping[];
}

const fetchStakeholderRelation = async ({
  compensationId,
}: {
  compensationId: number;
}): Promise<StakeholderRelationResponse> => {
  try {
    const url = endpoints.compensation.stakeholderRelation(compensationId);
    const res = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
    });

    return res;
  } catch (error) {
    console.error('Error while fetching stakeholder relation data:', error);
    throw error;
  }
};

export const useGetStakeholderRelation = ({ compensationId }: { compensationId: number }) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.compensation.stakeholderRelation, compensationId],
    queryFn: () => fetchStakeholderRelation({ compensationId }),
    enabled: !!compensationId && !checkStaticDataCompensationEnabled(),
  });

const updateStakeholderRelation = async ({
  compensationId,
  data,
}: {
  compensationId: number;
  data: UpdateStakeholderRelationRequest;
}) => {
  try {
    const url = endpoints.compensation.updateStakeholderRelation(compensationId);
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      body: data,
    });
    return response;
  } catch (error) {
    console.error('Error while updating stakeholder relation:', error);
    throw error;
  }
};

export const useUpdateStakeholderRelation = ({
  compensationId,
  onSuccess,
  onError,
}: {
  compensationId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: UpdateStakeholderRelationRequest) => updateStakeholderRelation({ compensationId, data }),
    onSuccess: () => {
      // Basic success handling
      queryClient.invalidateQueries([REACT_QUERY_KEYS.compensation.stakeholderRelation, compensationId]);
      toast({ title: 'Stakeholder relations updated successfully', status: 'success' });

      // Additional custom success callback
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('Error while updating stakeholder relation:', error);
      // Basic error handling
      toast({ title: error.message ?? 'Failed to update stakeholder relations', status: 'error' });

      // Additional custom error callback
      onError?.(error);
    },
  });
};

const fetchStakeholderMap = async ({ compensationId }: { compensationId: number }): Promise<StakeholderMapResponse> => {
  try {
    const url = endpoints.compensation.stakeholderMap(compensationId);
    const res = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
    });

    return res;
  } catch (error) {
    console.error('Error while fetching stakeholder map data:', error);
    throw error;
  }
};

export const useGetStakeholderMap = ({ compensationId }: { compensationId: number }) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.compensation.stakeholderMap, compensationId],
    queryFn: () => fetchStakeholderMap({ compensationId }),
    enabled: !!compensationId && !checkStaticDataCompensationEnabled(),
  });

const updateStakeholderMap = async ({
  compensationId,
  data,
}: {
  compensationId: number;
  data: UpdateStakeholderMapRequest;
}) => {
  try {
    const url = endpoints.compensation.updateStakeholderMap(compensationId);
    const response = await sendRequest({
      url,
      method: 'PUT',
      credentials: 'include',
      body: data,
    });
    return response;
  } catch (error) {
    console.error('Error while updating stakeholder map:', error);
    throw error;
  }
};

export const useUpdateStakeholderMap = ({
  compensationId,
  onSuccess,
  onError,
}: {
  compensationId: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: UpdateStakeholderMapRequest) => updateStakeholderMap({ compensationId, data }),
    onSuccess: () => {
      // Basic success handling
      queryClient.invalidateQueries([REACT_QUERY_KEYS.compensation.stakeholderMap, compensationId]);
      toast({ title: 'Stakeholder map updated successfully', status: 'success' });

      // Additional custom success callback
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('Error while updating stakeholder map:', error);
      // Basic error handling
      toast({ title: error.message ?? 'Failed to update stakeholder map', status: 'error' });

      // Additional custom error callback
      onError?.(error);
    },
  });
};
